import { Images } from "@assets/images";
import clsx from "clsx";
import {
  ChangeEvent,
  ForwardedRef,
  KeyboardEvent,
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormControlChildProps } from "../form-control";

interface InputProps extends FormControlChildProps {
  /**
   * Default: ''
   */
  value?: string;

  /**
   * Default: 'text'
   */
  type?: "text" | "password" | "number" | "date" | "datetime-local";

  /**
   * Default: ''
   */
  defaultValue?: string;

  /**
   * Default: ''
   */
  placeholder?: string;

  /**
   * Default: ''
   */
  className?: string;

  /**
   * Default: ''
   */
  inputClassName?: string;

  errorClassName?: string;

  width?: number | string;

  height?: number | string;

  /**
   * Default: true
   */
  disableAutoComplete?: boolean;

  /**
   * Default: undefined
   */
  defaultStatus?: undefined | "valid" | "inValid" | "warn";

  /**
   * Default: false
   */
  readOnly?: boolean;

  inputIcon?: string;

  inputElement?: ReactElement;

  /**
   * Default: ''
   */
  inputIconClassName?: string;

  /**
   * Default: ''
   */
  inputElementClassName?: string;

  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;

  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;

  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;

  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;

  onIconClick?: () => void;

  leftIcon?: string;

  onPaste?: (clipboardData: string, state: string | undefined) => string;

  maxLength?: number;

  onEnter?: (e: KeyboardEvent) => void;

  timeDelay?: number;
}

interface ForwardedRefProps {
  focus: () => void;
}

function Input(
  {
    id,
    formik,
    type = "text",
    value = "",
    defaultValue = "",
    className = "",
    inputClassName = "",
    name,
    disabled = false,
    placeholder = "",
    width,
    height = "[52px]",
    disableAutoComplete = true,
    status,
    defaultStatus = undefined,
    readOnly = false,
    errorClassName = "",
    inputIcon,
    inputIconClassName = "",
    inputElement,
    inputElementClassName = "",
    maxLength,
    leftIcon,
    onChange = () => {},
    onIconClick = () => {},
    onKeyDown = () => {},
    onBlur,
    fmOnChange,
    fmOnBlur,
    onFocus,
    onPaste,
    onEnter,
    timeDelay = 0,
  }: InputProps,
  ref: ForwardedRef<ForwardedRefProps>
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const refTimeOut = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [txValue, setTxValue] = useState(value || defaultValue);
  const [_status, changeStatus] = useState(status || defaultStatus);
  const [isShowPassword, setIsShowPassword] = useState(false);

  useImperativeHandle(ref, () => ({
    focus() {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
  }));

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (timeDelay) {
        if (refTimeOut.current) {
          clearTimeout(refTimeOut.current);
        }

        refTimeOut.current = setTimeout(() => {
          onChange && onChange(e);
        }, timeDelay);
      } else {
        onChange && onChange(e);
      }

      fmOnChange && fmOnChange(e);

      setTxValue(e.currentTarget.value);

      setTimeout(() => {
        if (formik) formik.setFieldTouched(name!, true);
      });
    },
    [fmOnChange, formik, name, onChange]
  );

  const handleBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onBlur && onBlur(e);

      fmOnBlur && fmOnBlur(e);
    },
    [fmOnBlur, onBlur]
  );

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (onPaste) {
      event.preventDefault();
      const pasteData = event.clipboardData.getData("text");
      const clipboardData = onPaste(pasteData, txValue);

      setTxValue(clipboardData);
    }
  };

  const contentMaxLength = useMemo(
    () => maxLength && maxLength + 1,
    [maxLength]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key == "Enter") {
      onEnter && onEnter(e);
    }

    onKeyDown && onKeyDown(e);
  };

  useEffect(() => {
    setTxValue(value || defaultValue);
  }, [defaultValue, value]);

  useEffect(() => {
    changeStatus(status || defaultStatus);
  }, [defaultStatus, status]);

  const isError = _status === "inValid" || _status === "warn";

  return (
    <div className={`box-border w-full ${className}`} style={{ width }}>
      <div
        className={`w-full flex relative h-${height} leading-${height} outline-none rounded-lg bg-color`}
      >
        {leftIcon && (
          <img
            className={"absolute w-4 h-4 left-[10px] top-[10px]"}
            src={leftIcon}
            alt=""
          />
        )}
        <input
          ref={inputRef}
          id={id}
          className={clsx(
            `w-full px-4 h-full bg-inherit text-color rounded-lg ${inputClassName}`,
            {
              "bg-[#A1A2A880]": disabled,
              "border-error": isError,
              "pr-8": type === "password",
              "pl-[50px]": !!inputIcon || !!inputElement,
              "pl-12": leftIcon,
            }
          )}
          type={isShowPassword ? "text" : type}
          value={txValue || ""}
          name={name}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={disableAutoComplete ? "off" : "on"}
          onFocus={onFocus}
          readOnly={readOnly}
          onPaste={handlePaste}
          maxLength={contentMaxLength}
        />
        {inputIcon && (
          <img
            className={`absolute w-4 h-4 left-[17px] top-[17px] ${inputIconClassName}`}
            src={inputIcon}
            alt=""
            onClick={onIconClick}
          />
        )}
        {inputElement && (
          <div
            className={`absolute w-4 h-4 left-3 top-2 ${inputElementClassName}`}
          >
            {inputElement}
          </div>
        )}
        {type === "password" && (
          <div
            className="absolute right-0 pr-[10px] h-full flex items-center cursor-pointer"
            onClick={() => setIsShowPassword(!isShowPassword)}
          >
            {isShowPassword ? (
              <img src={Images.EyeSlashIcon.default} alt="" />
            ) : (
              <img src={Images.EyeIcon.default} alt="" />
            )}
          </div>
        )}
      </div>
      {isError && (
        <div className={`flex error-color text-xs ${errorClassName}`}>
          {formik &&
            formik.getFieldMeta(name!).error &&
            formik.getFieldMeta(name!).error}
        </div>
      )}
    </div>
  );
}

export default forwardRef(Input);
